import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  EditOutlined,
  KeyOutlined,
  PlusOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { authFetch } from '../../utils/api';
import {
  getBrandLabel,
  tokenManager,
  type Brand,
  type BrandScope,
} from '../../utils/tokenManager';
import { useBrandOptions } from '../../hooks/useBrandOptions';

const { Title, Text } = Typography;

interface AdminRecord {
  id: number;
  username: string;
  brandScope: BrandScope;
  activeBrand: Brand;
  mustChangePassword: boolean;
  isPersonalAccount: boolean;
  createdAt?: string;
  updatedAt?: string;
}

type AdminFormValues = {
  username?: string;
  password?: string;
  scopeType: 'all' | 'scoped';
  brandScope?: Brand[];
  activeBrand?: Brand;
  mustChangePassword?: boolean;
  isPersonalAccount?: boolean;
};

function getScopeTags(scope: BrandScope) {
  if (scope === 'all') {
    return <Tag color="gold">全品牌</Tag>;
  }
  return (
    <Space size={4} wrap>
      {scope.map((brand) => (
        <Tag key={brand} color="blue">
          {getBrandLabel(brand)}
        </Tag>
      ))}
    </Space>
  );
}

function buildPayload(values: AdminFormValues) {
  const brandScope =
    values.scopeType === 'all' ? 'all' : values.brandScope ?? [];
  return {
    username: values.username?.trim(),
    password: values.password,
    brandScope,
    activeBrand: values.activeBrand,
    mustChangePassword: values.mustChangePassword ?? true,
    isPersonalAccount: values.isPersonalAccount ?? false,
  };
}

export const AdminList: React.FC = () => {
  const allBrandAdmin = tokenManager.isAllBrandAdmin();
  const brandOptions = useBrandOptions();
  const [data, setData] = useState<AdminRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [keyword, setKeyword] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AdminRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState<AdminRecord | null>(null);
  const [resetForm] = Form.useForm();
  const [form] = Form.useForm<AdminFormValues>();
  const scopeType = Form.useWatch('scopeType', form);
  const listGeneration = useRef(0);
  const listRequest = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    const generation = ++listGeneration.current;
    listRequest.current?.abort();
    if (!allBrandAdmin) return;
    const controller = new AbortController();
    listRequest.current = controller;
    setLoading(true);
    setData([]);
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (keyword) params.set('keyword', keyword);
      const res = await authFetch(`/api/admin/admins?${params.toString()}`, { signal: controller.signal });
      const result = await res.json();
      if (generation !== listGeneration.current || controller.signal.aborted) return;
      if (res.ok && result.code === 0 && result.data) {
        setData(Array.isArray(result.data.list) ? result.data.list : []);
        setTotal(result.data.total ?? 0);
      } else {
        setLoadError(result.msg || '讀取管理員失敗');
      }
    } catch (error) {
      if (generation === listGeneration.current && !controller.signal.aborted)
        setLoadError(error instanceof Error ? error.message : '讀取管理員失敗');
    } finally {
      if (generation === listGeneration.current && !controller.signal.aborted)
        setLoading(false);
    }
  }, [allBrandAdmin, keyword, page, pageSize]);

  useEffect(() => {
    void fetchData();
    return () => {
      listGeneration.current++;
      listRequest.current?.abort();
    };
  }, [fetchData]);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      scopeType: 'scoped',
      brandScope: [tokenManager.getActiveBrand()],
      activeBrand: tokenManager.getActiveBrand(),
      mustChangePassword: true,
      isPersonalAccount: false,
    });
    setModalOpen(true);
  };

  const openEdit = (record: AdminRecord) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      scopeType: record.brandScope === 'all' ? 'all' : 'scoped',
      brandScope: record.brandScope === 'all' ? [] : record.brandScope,
      activeBrand: record.activeBrand,
      mustChangePassword: record.mustChangePassword,
      isPersonalAccount: record.isPersonalAccount,
    });
    setModalOpen(true);
  };

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = buildPayload(values);
      const url = editing
        ? `/api/admin/admins/${editing.id}`
        : '/api/admin/admins';
      const method = editing ? 'PUT' : 'POST';
      const res = await authFetch(url, {
        method,
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result.code === 0) {
        message.success(editing ? '管理員已更新' : '管理員已建立');
        setModalOpen(false);
        fetchData();
      } else {
        message.error(result.msg || '操作失敗');
      }
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    if (!resetting) return;
    const values = await resetForm.validateFields();
    const res = await authFetch(`/api/admin/admins/${resetting.id}/password`, {
      method: 'PUT',
      body: JSON.stringify({ newPassword: values.newPassword }),
    });
    const result = await res.json();
    if (result.code === 0) {
      message.success('密碼已重設，該管理員需重新登入');
      setResetting(null);
      resetForm.resetFields();
      fetchData();
    } else {
      message.error(result.msg || '重設失敗');
    }
  };

  const remove = async (record: AdminRecord) => {
    const res = await authFetch(`/api/admin/admins/${record.id}`, {
      method: 'DELETE',
    });
    const result = await res.json();
    if (result.code === 0) {
      message.success('管理員已刪除');
      fetchData();
    } else {
      message.error(result.msg || '刪除失敗');
    }
  };

  if (!allBrandAdmin) {
    return (
      <div className="page-enter">
        <Title level={4}>管理員與品牌權限</Title>
        <Alert
          showIcon
          type="info"
          message="品牌管理員無法管理後台權限"
          description="此頁只開放全品牌管理員使用，避免品牌之間知道彼此存在。"
        />
      </div>
    );
  }

  return (
    <div className="page-enter">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          gap: 12,
        }}
      >
        <Space>
          <UserSwitchOutlined style={{ fontSize: 20 }} />
          <Title level={4} style={{ margin: 0 }}>
            管理員與品牌權限
          </Title>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
          新增管理員
        </Button>
      </div>

      <Alert
        showIcon
        type="warning"
        style={{ marginBottom: 12 }}
        message="帳號資料變更可能撤銷既有登入"
        description="變更品牌範圍或個人實名資料會撤銷既有登入。品牌管理員只管理授權品牌；全品牌管理員可管理所有品牌。"
      />

      {loadError && <Alert type="error" showIcon message="管理員資料讀取失敗" description={loadError} action={<Button onClick={() => void fetchData()}>重新讀取</Button>} />}
      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="搜尋管理員帳號"
          onSearch={(value) => {
            setPage(1);
            setKeyword(value.trim());
          }}
          style={{ width: 260, maxWidth: '100%' }}
        />
        <Button onClick={() => void fetchData()}>重新讀取管理員</Button>
      </Space>

      <Table
        dataSource={data}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1100 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (count) => `共 ${count} 筆`,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
        }}
      >
        <Table.Column dataIndex="id" title="ID" width={80} />
        <Table.Column dataIndex="username" title="帳號" width={180} />
        <Table.Column
          dataIndex="brandScope"
          title="品牌範圍"
          width={220}
          render={(scope: BrandScope) => getScopeTags(scope)}
        />
        <Table.Column
          dataIndex="activeBrand"
          title="預設品牌"
          width={120}
          render={(brand: Brand) => <Tag>{getBrandLabel(brand)}</Tag>}
        />
        <Table.Column
          dataIndex="mustChangePassword"
          title="強制改密"
          width={120}
          render={(value: boolean) =>
            value ? <Tag color="orange">是</Tag> : <Tag>否</Tag>
          }
        />
        <Table.Column
          dataIndex="isPersonalAccount"
          title={
            <Tooltip title="個人實名帳號才可拉取 raw client log（6 層安全控管的第 6 層）">
              <span>個人實名</span>
            </Tooltip>
          }
          width={120}
          render={(value: boolean) =>
            value ? <Tag color="green">是</Tag> : <Tag>否</Tag>
          }
        />
        <Table.Column
          title="操作"
          width={260}
          fixed="right"
          render={(_: unknown, record: AdminRecord) => (
            <Space wrap>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(record)}
              >
                編輯
              </Button>
              <Button
                size="small"
                icon={<KeyOutlined />}
                onClick={() => {
                  setResetting(record);
                  resetForm.resetFields();
                }}
              >
                重設密碼
              </Button>
              <Popconfirm
                title={`確定刪除 ${record.username}？`}
                onConfirm={() => remove(record)}
              >
                <Button size="small" danger>
                  刪除
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>

      <Modal
        title={editing ? `編輯管理員：${editing.username}` : '新增管理員'}
        open={modalOpen}
        onOk={save}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          {!editing ? (
            <>
              <Form.Item
                name="username"
                label="帳號"
                rules={[{ required: true, message: '請輸入管理員帳號' }]}
              >
                <Input autoComplete="off" />
              </Form.Item>
              <Form.Item
                name="password"
                label="初始密碼"
                rules={[{ required: true, message: '請輸入初始密碼' }]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
            </>
          ) : (
            <Text type="secondary">管理員帳號不可改名；需要更名時請新增帳號後刪除舊帳號。</Text>
          )}

          <Form.Item
            name="scopeType"
            label="權限範圍"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: 'scoped', label: '指定品牌' },
                { value: 'all', label: '全品牌管理員' },
              ]}
            />
          </Form.Item>
          {scopeType !== 'all' ? (
            <Form.Item
              name="brandScope"
              label="可管理品牌"
              rules={[{ required: true, message: '請選擇至少一個品牌' }]}
            >
              <Select mode="multiple" options={brandOptions} />
            </Form.Item>
          ) : null}
          <Form.Item
            name="activeBrand"
            label="預設品牌"
            rules={[{ required: true, message: '請選擇預設品牌' }]}
          >
            <Select options={brandOptions} />
          </Form.Item>
          <Form.Item
            name="mustChangePassword"
            label="下次登入強制改密"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="isPersonalAccount"
            label={
              <Tooltip title="個人實名帳號（必勾才能拉 raw client log）— 共用 / 服務帳號禁勾，這是 raw 匯出 6 層安全的第 6 層">
                <span>個人實名帳號</span>
              </Tooltip>
            }
            valuePropName="checked"
            extra="僅限定向到單一管理員的實名帳號才可勾選；shared / 自動化帳號保持關閉。"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={resetting ? `重設密碼：${resetting.username}` : '重設密碼'}
        open={!!resetting}
        onOk={resetPassword}
        onCancel={() => setResetting(null)}
        destroyOnClose
      >
        <Form form={resetForm} layout="vertical">
          <Form.Item
            name="newPassword"
            label="新密碼"
            rules={[{ required: true, message: '請輸入新密碼' }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
